import React, { useId, useState } from "react";
import styled, { css } from "styled-components";
import {
  ChevronDown,
  FlaskConical,
  KeyRound,
  ShieldCheck,
  Zap,
} from "lucide-react";
import IntercomLink from "common/Text/IntercomLink";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import { API_KEYS_ARTICLE } from "@shared/features/onboarding/constants";
import { ExtraInfoType } from "@shared/features/onboarding/types";
import { ConfigurationExchangeInfo, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import useConnectExchangeExtraInfo from "features/portfolio/CreateAccount/useConnectExchangeExtraInfo";
import { focusRing } from "features/portfolio/CreateAccount/NewLook/styles";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

interface NewLookConnectHelpProps {
  primaryExchange?: string;
  selectedSubExchange?: ConfigurationExchangeInfo;
  collapsible?: boolean;
}

export const NewLookSecurityNote = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  return (
    <SecurityNote>
      <ShieldCheck size={18} strokeWidth={NEW_LOOK_ICON_STROKE} aria-hidden />
      <div>
        <NoteTitle>{t("create_account.new_look.security_title")}</NoteTitle>
        <NoteText>
          {t("create_account.new_look.security_body")}{" "}
          <IntercomLink articleId={API_KEYS_ARTICLE} />
        </NoteText>
      </div>
    </SecurityNote>
  );
};

const NewLookConnectHelp = ({
  primaryExchange,
  selectedSubExchange,
  collapsible,
}: NewLookConnectHelpProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();
  const {
    isDemoWorkspace,
    extraInfoToTitle,
    demoAccountExtraInfo,
    showSignUpExchangeLink,
    apiKeysGuide,
    signUpExchangeLink,
  } = useConnectExchangeExtraInfo({
    primaryExchange,
    selectedSubExchange,
    isQuickConnect: false,
  });

  const title = isDemoWorkspace
    ? demoAccountExtraInfo.title
    : t("create_account.new_look.help_title");
  const HeaderIcon = isDemoWorkspace ? FlaskConical : KeyRound;

  const steps = (
    <>
      <Steps>
        {apiKeysGuide.items.map((item, index) => (
          <Step key={index}>
            <StepNumber>{index + 1}</StepNumber>
            <StepText>{item}</StepText>
          </Step>
        ))}
      </Steps>
      {apiKeysGuide.note && <StepsNote>{apiKeysGuide.note}</StepsNote>}
      {apiKeysGuide.createApiLink && (
        <IntercomLink
          articleLink={apiKeysGuide.createApiLink}
          style={{ display: "inline-block", fontSize: 13 }}
        />
      )}
    </>
  );

  const content = isDemoWorkspace ? (
    <DemoContent>{demoAccountExtraInfo.content}</DemoContent>
  ) : (
    <>
      {steps}
      {showSignUpExchangeLink && (
        <SignUp>
          {extraInfoToTitle[ExtraInfoType.NO_EXCHANGE_ACCOUNT]}{" "}
          {signUpExchangeLink}
        </SignUp>
      )}
    </>
  );

  const header = (
    <>
      <HeaderIconTile>
        <HeaderIcon size={16} strokeWidth={NEW_LOOK_ICON_STROKE} aria-hidden />
      </HeaderIconTile>
      <HeaderText>
        <PanelTitle>{title}</PanelTitle>
        {!isDemoWorkspace && !!apiKeysGuide.items.length && (
          <PanelMeta>
            {t("create_account.new_look.help_steps", {
              count: apiKeysGuide.items.length,
            })}
          </PanelMeta>
        )}
      </HeaderText>
    </>
  );

  if (collapsible) {
    return (
      <Panel $compact={true}>
        <ToggleHeader
          type="button"
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setExpanded(!expanded)}
        >
          {header}
          <Chevron $expanded={expanded}>
            <ChevronDown
              size={16}
              strokeWidth={NEW_LOOK_ICON_STROKE}
              aria-hidden
            />
          </Chevron>
        </ToggleHeader>
        {expanded && (
          <Content id={contentId} $inset={true}>
            {content}
          </Content>
        )}
      </Panel>
    );
  }

  return (
    <Panel as="aside">
      <StaticHeader>{header}</StaticHeader>
      <Content>{content}</Content>
    </Panel>
  );
};

export default NewLookConnectHelp;

/** Quick connect's counterpart to the API keys guide (wide onboarding). */
export const NewLookQuickConnectSteps = ({
  primaryExchange,
}: {
  primaryExchange?: string;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const steps = [
    t("create_account.new_look.quick_step_login", {
      exchange: primaryExchange,
    }),
    t("create_account.new_look.quick_step_keys"),
    t("create_account.new_look.quick_step_done"),
  ];
  return (
    <Panel as="aside">
      <StaticHeader>
        <HeaderIconTile>
          <Zap size={16} strokeWidth={NEW_LOOK_ICON_STROKE} aria-hidden />
        </HeaderIconTile>
        <HeaderText>
          <PanelTitle>
            {t("create_account.new_look.quick_steps_title")}
          </PanelTitle>
        </HeaderText>
      </StaticHeader>
      <Content>
        <Steps>
          {steps.map((step, index) => (
            <Step key={index}>
              <StepNumber>{index + 1}</StepNumber>
              <StepText>{step}</StepText>
            </Step>
          ))}
        </Steps>
      </Content>
    </Panel>
  );
};

const Panel = styled.div<{ $compact?: boolean }>`
  box-sizing: border-box;
  border: ${(p) => p.theme.borders.panel};
  box-shadow: ${(p) => p.theme.shadows.panel};
  border-radius: ${(p) => p.theme.radii.card}px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  padding: ${(p) => (p.$compact ? "4px" : "20px")};
  min-width: 0;
`;
const headerRow = css`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;
const StaticHeader = styled.div`
  ${headerRow};
  margin-bottom: 16px;
`;
const ToggleHeader = styled.button`
  ${headerRow};
  padding: 8px 12px;
  border: 0;
  border-radius: ${(p) => p.theme.radii.card - 4}px;
  background: transparent;
  font: inherit;
  text-align: left;
  color: inherit;
  cursor: pointer;
  &:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  ${focusRing};
`;
const HeaderIconTile = styled.span`
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 20px;
  height: 20px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const HeaderText = styled.span`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;
const PanelTitle = styled.span`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const PanelMeta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Chevron = styled.span<{ $expanded: boolean }>`
  flex: none;
  display: inline-flex;
  color: ${(p) => p.theme.colors.secondaryText};
  transition: transform 200ms ease;
  transform: rotate(${(p) => (p.$expanded ? 180 : 0)}deg);
`;
const Content = styled.div<{ $inset?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  min-width: 0;
  padding: ${(p) => (p.$inset ? "4px 12px 12px" : "0")};
`;
const Steps = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Step = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;
const StepNumber = styled.span`
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${(p) => getNewLookTokens(p.theme).well};
  border: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-size: 12px;
  line-height: 1;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
`;
const StepText = styled.span`
  flex: 1;
  min-width: 0;
  padding-top: 1px;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).text};
`;
const StepsNote = styled.div`
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12.5px;
  line-height: 18px;
  font-style: italic;
`;
const SecurityNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-radius: ${(p) => p.theme.radii.control}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  > svg {
    flex: none;
    margin-top: 1px;
    color: ${(p) => getNewLookTokens(p.theme).secondary};
  }
`;
const NoteTitle = styled.div`
  ${newLookType.label};
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const NoteText = styled.div`
  margin-top: 2px;
  ${newLookType.meta};
  font-size: 12.5px;
  line-height: 18px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  a {
    font-size: 12.5px;
  }
`;
const SignUp = styled.div`
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 13px;
  line-height: 19px;
  a {
    font-size: 13px;
  }
`;
const DemoContent = styled.div`
  color: ${(p) => p.theme.colors.primaryText};
  font-size: 13.5px;
  line-height: 20px;
`;
